
/*
 * The design assistant, client half. Pure functions (window.RRAssist in the page, module.exports in node):
 *   - the catalogue of what the designer can do, in the exact words the server's schema uses
 *   - applyActions: a reply's design actions applied to a COPY of the state, every value checked
 *   - the offline demo, so the flow can be tried with no server at all
 *   - the request to Rivers Refined: the payload the inbound webhook receives
 * The network calls themselves live in app.js (assistFetch), the only place the page talks to a server.
 */
(function (root) {
'use strict';
const isNode = typeof module !== 'undefined' && module.exports;
const G = isNode ? require('./geometry.js') : root.RRGeom;
const St = isNode ? require('./state.js') : root.RRState;

// The one line for anything that is not design. Fixed, so it can be tested word for word.
const REFUSAL = "We'll go over that with you directly when we send your quote.";
const WALLS = ['front', 'left', 'right', 'back', 'ceiling'];
const keys = (list) => list.map(x => x[0]);

/** Every value an action may take, by path. The server's JSON schema is checked against this in tests. */
const PATHS = {
  'room.width': 'length', 'room.length': 'length', 'room.height': 'length',
  'room.floor': ['carpet', 'wood'],
  'light': ['day', 'theater'],
  'layout': ['full', 'wainscot', 'feature'],
  'zone': ['lower', 'upper'],
  'pattern': () => keys(G.DESIGN_TYPES).filter(t => t !== 'custom'),
  'moduleW': 'length', 'moduleH': 'length', 'size': 'length', 'targetW': 'length', 'targetH': 'length',
  'count': 'number', 'rows': 'number', 'cols': 'number', 'angle': 'number',
  'fabric': () => St.FABRICS.map(f => f.name),
  'fabric.family': ['velvet', 'linen', 'leather'],
  'accent': ['on', 'off'],
  'accent.fabric': () => St.FABRICS.map(f => f.name),
  'accent.mode': ['alternate', 'center'],
  'inlay': () => keys(G.INLAY_TYPES),
  'lineScope': () => keys(G.LINE_SCOPES),
  'led': () => keys(G.LED_TYPES),
  'ledTemp': () => keys(G.LED_TEMPS),
  'mirror': ['on', 'off'],
  'surface': () => keys(G.SURFACE_TYPES),
  'stars': 'number',
  'wainscot.height': 'length',
  'sections.left': () => keys(G.SECTION_TYPES), 'sections.right': () => keys(G.SECTION_TYPES),
  'sections.top': () => keys(G.SECTION_TYPES), 'sections.bottom': () => keys(G.SECTION_TYPES),
  'sections.center': () => keys(G.CENTER_TYPES),
  'sections.width': 'length', 'sections.topHeight': 'length', 'sections.bottomHeight': 'length',
  'sections.wood': () => keys(G.WOODS),
  'sconces': ['on', 'off'], 'sconces.count': 'number',
  'sconces.style': () => keys(G.SCONCE_STYLES), 'sconces.finish': () => keys(G.METALS),
  'cove': () => keys(G.COVE_TYPES),
  'headboard': ['on', 'off'], 'headboard.bed': () => keys(G.BED_SIZES), 'headboard.height': 'length',
  'copyToAllWalls': ['yes'],
};
function allowed(path) { const p = PATHS[path]; return typeof p === 'function' ? p() : p; }

/** What the server needs to know about this build, as plain lists (tests compare them with the server's own copy). */
function catalog() {
  const out = { presets: St.PRESET_LIST.map(p => p[0]), walls: WALLS, tiles: keys(G.CUSTOM_TILES), paths: {} };
  for (const k of Object.keys(PATHS)) { const a = allowed(k); out.paths[k] = Array.isArray(a) ? a : a; }
  return out;
}

const clone = (o) => JSON.parse(JSON.stringify(o));
const r16 = (x) => Math.round(x * 16) / 16;
function lengthOf(v, lo, hi) {
  const n = typeof v === 'number' ? v : G.parseLength(String(v));
  return isFinite(n) && n !== null ? Math.min(hi, Math.max(lo, r16(n))) : null;
}

/** The pattern change the picker makes, as a pure function: keeps colour, family, accent and lighting. */
function setPattern(w, z, type) {
  const old = w.zones[z];
  if (old.type === type) return false;
  const keep = { color: old.color, family: old.family, accentOn: old.accentOn, accentColor: old.accentColor, accentFamily: old.accentFamily, accentMode: old.accentMode,
    led: old.led, ledTemp: old.ledTemp, ledLevel: old.ledLevel, surface: old.surface, stars: old.stars, lineScope: old.lineScope };
  const def = G.defaultDesign(type);
  if (def.inlay === 'none') keep.inlay = old.inlay;
  if (def.surface !== 'plain') keep.surface = def.surface;
  const d = St.design(type, keep);
  if (type === 'grid') { d.mode = 'size'; d.targetW = 24; d.targetH = 36; }
  if (type === 'channels' || type === 'bands') { d.mode = 'size'; d.size = St.SIZE_MODELS[type].chips[1][1]; }
  if (old.type === 'none' && type !== 'none' && old.color === G.defaultDesign('none').color) { d.color = '#2B2B2E'; d.family = 'linen'; }
  w.zones[z] = d;
  return true;
}
/** Full wall, wainscot, or a feature with sections, as the Layout tiles do it. surfaceW = the wall's width. */
function setLayout(w, mode, surfaceW, roomH) {
  const cur = w.comp ? 'feature' : w.wainscot ? 'wainscot' : 'full';
  if (cur === mode) return false;
  if (mode === 'feature') {
    w.comp = Object.assign(G.defaultComp(), { width: Math.round(Math.min(surfaceW * 0.55, Math.max(60, surfaceW * 0.45))) });
    if (w.wainscot) { w.wainscot = false; if (w.zones[0].type === 'none' || w.zones[0].type === 'grid') w.zones[0] = w.zones[1]; }
    if (w.zones[0].type === 'none') w.zones[0] = St.design('channels', { mode: 'size', size: 16, color: '#2B2B2E', family: 'linen' });
  } else if (mode === 'wainscot') {
    w.comp = null; w.wainscot = true;
    if (!(w.split >= 18 && w.split <= roomH - 24)) w.split = 40;
    if (w.zones[0].type === 'none' && w.zones[1].type === 'none') w.zones[1] = St.design('channels', { mode: 'size', size: 16 });
    if (w.zones[0].type !== 'none' && (!w.zones[1] || w.zones[1].type === 'channels')) {
      const up = w.zones[0];
      w.zones[1] = up;
      w.zones[0] = St.design('grid', { mode: 'size', targetW: 30, targetH: 40, color: up.color, family: up.family });
    }
  } else { w.comp = null; w.wainscot = false; }
  return true;
}

/**
 * Apply one reply's actions to a COPY of the state. Nothing unchecked reaches the design:
 * an unknown path, a value outside its list or a custom pattern we could not build is skipped
 * and reported, never guessed at. Returns { state, wall, applied: [words], skipped: [words], custom: {ok, errors} | null }.
 */
const HEADBOARD_HIDES_SECTIONS = 'the centre and side sections, because a headboard wall does not show them';
function applyActions(state0, actions, opts) {
  opts = opts || {};
  let state = clone(state0);
  const applied = [], skipped = [];
  const a = actions && typeof actions === 'object' ? actions : {};
  let wall = WALLS.includes(opts.wall) ? opts.wall : 'front';
  if (a.preset != null && a.preset !== '') {
    if (St.PRESETS[a.preset] && St.PRESET_LIST.some(p => p[0] === a.preset)) { state = St.PRESETS[a.preset](); applied.push('room type ' + a.preset); wall = (St.PRESET_LIST.find(p => p[0] === a.preset)[3]) || 'front'; }
    else skipped.push('room type "' + a.preset + '"');
  }
  if (a.wall != null && a.wall !== '') { if (WALLS.includes(a.wall)) wall = a.wall; else skipped.push('wall "' + a.wall + '"'); }
  const W = () => state.walls[wall];
  let zone = null;
  // The zone a change lands on is the one the visitor can see: the upper zone of a wainscot, and on a
  // feature wall whose centre is not upholstered (a mirror, slats...) the second pattern its sections
  // carry. Otherwise "green velvet" went to a field the wall does not draw (15 Oct 2026, live test).
  const sidesOnly = () => { const c = W().comp; return !!c && c.center !== 'pattern' && ['left', 'right', 'top', 'bottom'].some(k => c[k] === 'pattern'); };
  const z = () => zone != null ? zone : (W().wainscot || sidesOnly() ? 1 : 0);
  const D = () => W().zones[z()];
  const surf = () => St.surfaceSize(state, wall);
  const sets = Array.isArray(a.set) ? a.set.slice(0, 40) : [];
  for (const it of sets) {
    const path = it && String(it.path || ''), raw = it ? it.value : undefined, val = String(raw == null ? '' : raw).trim();
    const al = PATHS[path] ? allowed(path) : null;
    const no = () => skipped.push(path + ' = ' + val.slice(0, 40));
    if (!al) { no(); continue; }
    if (Array.isArray(al) && !al.includes(val)) { no(); continue; }
    const on = val === 'on' || val === 'yes';
    const isWall = wall !== 'ceiling';
    switch (path) {
      case 'room.width': case 'room.length': case 'room.height': {
        const k = { 'room.width': 'W', 'room.length': 'L', 'room.height': 'H' }[path], v = lengthOf(val, 24, k === 'H' ? 360 : 1200);
        if (v == null) { no(); continue; } state.room[k] = v; break;
      }
      case 'room.floor': state.room.floor = val; break;
      case 'light': state.light = val; break;
      case 'layout': if (!isWall && val !== 'full') { no(); continue; } setLayout(W(), val, surf().w, state.room.H); break;
      case 'zone': if (!W().wainscot) { no(); continue; } zone = val === 'lower' ? 0 : 1; break;
      case 'pattern': setPattern(W(), z(), val); break;
      case 'moduleW': case 'moduleH': case 'size': case 'targetW': case 'targetH': {
        const v = lengthOf(val, 2, 240); if (v == null) { no(); continue; }
        D()[path] = v; if (path === 'size' || path === 'targetW' || path === 'targetH') D().mode = 'size'; break;
      }
      case 'count': case 'rows': case 'cols': case 'angle': case 'stars': {
        const n = +val, lim = { count: [0, 240], rows: [1, 60], cols: [1, 60], angle: [5, 80], stars: [0, 12] }[path];
        if (!isFinite(n)) { no(); continue; }
        D()[path] = Math.min(lim[1], Math.max(lim[0], path === 'angle' || path === 'stars' ? n : Math.round(n)));
        if (path === 'rows' || path === 'cols' || path === 'count') D().mode = 'count';
        break;
      }
      case 'fabric': { const f = St.FABRICS.find(x => x.name === val); D().family = f.family; D().color = f.hex; break; }
      case 'fabric.family': D().family = val; break;
      case 'accent': D().accentOn = on; break;
      case 'accent.fabric': { const f = St.FABRICS.find(x => x.name === val); D().accentOn = true; D().accentFamily = f.family; D().accentColor = f.hex; break; }
      case 'accent.mode': D().accentMode = val; break;
      case 'inlay': case 'lineScope': case 'led': case 'ledTemp': case 'surface': D()[path] = val; break;
      case 'mirror': D().mirror = on; if (on && !G.MIRROR_TYPES.includes(D().type)) { skipped.push('mirror inserts need a pattern with mirror pieces: ' + G.MIRROR_TYPES.join(', ')); } break;
      case 'wainscot.height': { const v = lengthOf(val, 18, state.room.H - 24); if (v == null || !W().wainscot) { no(); continue; } W().split = v; break; }
      case 'sections.left': case 'sections.right': case 'sections.top': case 'sections.bottom': case 'sections.center':
      case 'sections.width': case 'sections.topHeight': case 'sections.bottomHeight': case 'sections.wood': {
        if (!isWall) { no(); continue; }
        if (!W().comp) setLayout(W(), 'feature', surf().w, state.room.H);
        const c = W().comp, k = path.split('.')[1];
        if (k === 'width') { const v = lengthOf(val, 12, surf().w); if (v == null) { no(); continue; } c.width = v; }
        else if (k === 'topHeight' || k === 'bottomHeight') { const v = lengthOf(val, 0, state.room.H / 2); if (v == null) { no(); continue; } c[k === 'topHeight' ? 'height' : 'bottomH'] = v; }
        else if (k === 'wood') c.wood = val;
        else { c[k] = val; if ((k === 'left' || k === 'right') && c.link) c[k === 'left' ? 'right' : 'left'] = val; }
        break;
      }
      case 'sconces': if (!isWall) { no(); continue; } W().sconces = on ? (W().sconces || G.defaultSconces()) : null; break;
      case 'sconces.count': case 'sconces.style': case 'sconces.finish': {
        if (!isWall) { no(); continue; }
        if (!W().sconces) W().sconces = G.defaultSconces();
        const k = path.split('.')[1];
        if (k === 'count') { const n = Math.round(+val); if (!(n >= 1 && n <= 6)) { no(); continue; } W().sconces.n = n; }
        else W().sconces[k === 'style' ? 'style' : 'finish'] = val;
        break;
      }
      case 'cove': W().cove = val; break;
      case 'headboard': case 'headboard.bed': case 'headboard.height': {
        if (!isWall) { no(); continue; }
        if (path === 'headboard' && !on) { W().headboard = null; break; }
        if (!W().headboard) { W().headboard = G.defaultHeadboard(); W().comp = null; W().wainscot = false; }
        if (path === 'headboard.bed') W().headboard.bed = val;
        if (path === 'headboard.height') { const v = lengthOf(val, 30, Math.min(96, state.room.H)); if (v == null) { no(); continue; } W().headboard.height = v; }
        break;
      }
      case 'copyToAllWalls': {
        const src = W();
        for (const k of ['front', 'left', 'right', 'back']) if (k !== wall) { const dst = state.walls[k]; dst.wainscot = src.wainscot; dst.split = src.split; dst.zoneGap = src.zoneGap; dst.zones = clone(src.zones); dst.comp = src.comp ? clone(src.comp) : null; }
        break;
      }
      default: no(); continue;
    }
    applied.push(path + ' ' + val);
  }
  // A wall with a headboard does not draw its sections, so a section set alongside one would be
  // reported as done while nothing on screen changed. Say so instead (15 Oct 2026, first live test).
  if (W() && W().headboard) {
    const hidden = applied.filter(x => x.startsWith('sections.'));
    if (hidden.length) {
      for (const x of hidden) applied.splice(applied.indexOf(x), 1);
      skipped.push(HEADBOARD_HIDES_SECTIONS);
    }
  }
  // a custom pattern: built only if the engine says it can be built at this size
  let custom = null;
  if (a.custom && typeof a.custom === 'object') {
    const d0 = D(), w = lengthOf(a.custom.moduleW != null ? a.custom.moduleW : 24, 4, 240) || 24, h = lengthOf(a.custom.moduleH != null ? a.custom.moduleH : 24, 4, 240) || 24;
    const v = G.validateCustom(a.custom, { moduleW: w, moduleH: h, minPiece: state.minPiece >= 0 ? state.minPiece : 3 });
    custom = { ok: v.ok, errors: v.errors };
    if (v.ok) {
      const d = St.design('custom', { color: d0.color, family: d0.family, accentOn: d0.accentOn, accentColor: d0.accentColor, accentFamily: d0.accentFamily,
        inlay: d0.inlay, lineScope: d0.lineScope, led: d0.led, ledTemp: d0.ledTemp, surface: d0.surface, moduleW: w, moduleH: h });
      d.custom = v.info.spec;
      W().zones[z()] = d;
      applied.push('custom pattern ' + v.info.spec.name);
    }
  }
  return { state, wall, applied, skipped, custom };
}

// ------------------------------------------------------------ the offline demo
// Canned answers in the same shape the server sends, so the whole flow (apply, custom pattern,
// send) can be tried in a local preview. It never claims to be the real assistant.
const DEMO_CUSTOM = { name: 'Arched waves', tile: 'brick', moduleW: 24, moduleH: 30, shapes: [
  [[0, 0], [1, 0], [1, 0.333, 0.2], [0, 0.333]],
  [[0, 0.333, -0.2], [1, 0.333], [1, 0.667, 0.2], [0, 0.667]],
  [[0, 0.667, -0.2], [1, 0.667], [1, 1], [0, 1]],
] };
const DEMO_OFF = /\$|price|cost|how much|quote|estimate|budget|cheap|afford|pay|deposit|financ|discount|warrant|guarantee|schedul|when can|how long|how soon|lead time|timeline|start date|availab|ignore (all |your |previous )|system prompt|pretend|jailbreak/i;
function demoReply(text) {
  const t = String(text || '').toLowerCase();
  const out = (reply, actions, ready) => ({ reply, topic: 'design', actions: Object.assign({ preset: null, wall: null, set: [], custom: null, closest: null }, actions || {}), ready: !!ready, demo: true });
  if (DEMO_OFF.test(t)) return { reply: REFUSAL, topic: 'off_scope', actions: { preset: null, wall: null, set: [], custom: null, closest: null }, ready: false, demo: true };
  if (/happy|perfect|that's it|thats it|love it|send (it|this)|looks great|we're done|done/.test(t)) return out('We are glad it is where you want it. Send it to us below and we will prepare your quote.', {}, true);
  if (/custom|don't show|dont show|not (in|on) the list|wave|scallop|own pattern|something different/.test(t)) {
    return out('We drew a pattern for this: stacked bands with arched joints, set half over on every row. Each band is a separate panel, so the curves are cut, not stitched. We can make the bands taller or the arches gentler.', { custom: DEMO_CUSTOM });
  }
  const set = [], says = [];
  const pick = (re, path, value, words) => { if (re.test(t)) { set.push({ path, value }); says.push(words); } };
  let preset = null;
  if (/theater|theatre|cinema|movie/.test(t)) { preset = 'theater'; says.push('a home theater to start from'); }
  else if (/headboard|bed/.test(t)) { preset = 'bedroom'; says.push('the bedroom wall to start from'); }
  pick(/hexagon|hex\b/, 'pattern', 'hexes', 'a hexagon lattice');
  pick(/channel/, 'pattern', 'channels', 'vertical channels');
  pick(/diamond/, 'pattern', 'diamonds', 'diamonds');
  pick(/herringbone|fishbone/, 'pattern', 'fishbone', 'a fishbone');
  pick(/slat/, 'sections.left', 'slats', 'wood slats either side');
  pick(/mirror/, 'pattern', 'ashlar', 'an ashlar stack, which takes mirror inserts');
  pick(/mirror/, 'mirror', 'on', 'mirror inserts');
  pick(/gold/, 'inlay', 'gold', 'a gold inlay in the reveals');
  pick(/\bled\b|glow|light/, 'led', 'lines', 'LED light in the reveals');
  for (const f of St.FABRICS) if (new RegExp('\\b' + f.name.toLowerCase() + '\\b').test(t)) { set.push({ path: 'fabric', value: f.name }); says.push(f.name + ' ' + f.family); break; }
  pick(/bigger|larger|wider/, 'moduleW', '30', 'larger panels');
  pick(/smaller|narrower|tighter/, 'moduleW', '12', 'smaller panels');
  if (!preset && !set.length) return out('We can help with the layout, the pattern, the fabric, mirrors, wood slats and lighting. Tell us about the room and the look you have in mind, or describe a pattern we do not show.');
  return out('We set up ' + says.join(', ') + '. Tell us what to change next.', { preset, set });
}

// ------------------------------------------------------------ sending the design to Rivers Refined
function cleanField(v, max) { return String(v == null ? '' : v).replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, max); }
/** The body the inbound webhook receives for channel design_request. Pure; the page adds the Turnstile token. */
function requestPayload(form, link, summary, transcript) {
  return {
    name: cleanField(form.name, 120), email: cleanField(form.email, 200), phone: cleanField(form.phone, 40), notes: cleanField(form.notes, 2000),
    link: cleanField(link, 6000), summary: cleanField(summary, 4000),
    transcript: (Array.isArray(transcript) ? transcript : []).slice(-40).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', text: cleanField(m.text, 1500) })),
    website: cleanField(form.website, 200),
  };
}
function validRequest(form) {
  const errs = [];
  if (!cleanField(form.name, 120)) errs.push('Please add your name.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleanField(form.email, 200))) errs.push('Please add an email address we can reply to.');
  return errs;
}

const api = { REFUSAL, HEADBOARD_HIDES_SECTIONS, WALLS, PATHS, allowed, catalog, setPattern, setLayout, applyActions, DEMO_CUSTOM, demoReply, requestPayload, validRequest };
if (isNode) module.exports = api;
else root.RRAssist = api;
})(typeof window !== 'undefined' ? window : globalThis);

